import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { t, type IntlService } from 'ember-intl';
import { PhFileText } from 'ember-phosphor-icons';
import Chip, { type ChipIntent } from 'tio-ui/components/chip';
import { formatShortDate } from 'tio-ui/format';
import BenefitCard from 'tio-employee/components/dashboard-kit/benefit-card';
import type {
  EligibilityRun,
  EligibilityRunPhase,
} from 'tio-employee/utils/partner-dashboard/sources/eligibility';
import type { WidgetComponentSignature } from 'tio-employee/utils/partner-dashboard/registry';

const PHASES: Record<EligibilityRunPhase, { key: string; intent: ChipIntent }> = {
  awaitingYou: { key: 'awaiting_you', intent: 'warning' },
  awaitingReview: { key: 'awaiting_review', intent: 'primary' },
  failed: { key: 'failed', intent: 'danger' },
  aborted: { key: 'aborted', intent: 'default' },
  completed: { key: 'completed', intent: 'success' },
  inProgress: { key: 'in_progress', intent: 'primary' },
};

/** Pertinent card: the latest eligibility file, where it stands and what it changed (or would change). */
export default class EligibilityLatestRunWidget extends Component<
  WidgetComponentSignature<EligibilityRun | null>
> {
  @service declare intl: IntlService;

  get phase() {
    const run = this.args.data;
    return run ? PHASES[run.phase] : null;
  }

  get phaseLabel() {
    return this.phase ? this.intl.t(`partner_dashboard.eligibility.phase_${this.phase.key}`) : '';
  }

  get stats() {
    const run = this.args.data;
    if (!run?.counts) return [];
    const { created, updated, deactivated, reactivated } = run.counts;
    const stats = [
      { key: 'created', value: created },
      { key: 'updated', value: updated },
      { key: 'deactivated', value: deactivated },
      { key: 'reactivated', value: reactivated },
      ...(run.errors ? [{ key: 'errors', value: run.errors }] : []),
    ];
    return stats.map((stat) => ({
      ...stat,
      label: this.intl.t(`partner_dashboard.eligibility.count_${stat.key}`),
    }));
  }

  <template>
    <BenefitCard
      @title={{t "partner_dashboard.eligibility.title"}}
      @icon={{PhFileText}}
      @actionLabel={{t "partner_dashboard.eligibility.history_action"}}
      @actionRoute="authenticated.admin.eligibility-files.index"
      data-test-partner-dashboard-eligibility-run
    >
      {{#if @data}}
        <div class="tio-col gap-3">
          <div class="tio-row-between flex-wrap gap-2">
            <div class="tio-col-extra-tight tio-fill-min">
              <LinkTo
                @route="authenticated.admin.eligibility-files.show"
                @model={{@data.id}}
                class="tio-text-base-semibold app-text-link break-all"
              >{{@data.fileName}}</LinkTo>
              {{#if @data.startedAt}}
                <span class="tio-text-sm-medium tio-fg-secondary">
                  {{t
                    "partner_dashboard.eligibility.started"
                    date=(formatShortDate @data.startedAt)
                  }}
                </span>
              {{/if}}
            </div>
            {{#if this.phase}}
              <Chip @intent={{this.phase.intent}} @appearance="faded" @size="sm">
                {{this.phaseLabel}}
              </Chip>
            {{/if}}
          </div>
          {{#if this.stats.length}}
            <dl class="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(140px,1fr))]">
              {{#each this.stats key="key" as |stat|}}
                <div
                  class="tio-col-extra-tight"
                  data-test-partner-dashboard-eligibility-stat={{stat.key}}
                >
                  <dt class="tio-text-sm-medium tio-fg-secondary">{{stat.label}}</dt>
                  <dd class="tio-text-lg-semibold tio-fg-primary">{{stat.value}}</dd>
                </div>
              {{/each}}
            </dl>
            {{#if @data.isPreview}}
              <p class="tio-text-sm-medium tio-fg-secondary">
                {{t "partner_dashboard.eligibility.preview_note"}}
              </p>
            {{/if}}
          {{/if}}
        </div>
      {{else}}
        <p class="tio-text-sm-medium tio-fg-secondary">{{t
            "partner_dashboard.eligibility.empty"
          }}</p>
      {{/if}}
    </BenefitCard>
  </template>
}
